import { Component, Input, Output, EventEmitter, OnInit, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Ticket, TipoTicketDetailed, Precio } from '../../core/services/tickets.service';

/**
 * Estado de un asiento en el mapa
 */
export type SeatStatus = 'available' | 'selected' | 'reserved' | 'cart-reserved' | 'sold' | 'validated' | 'cancelled' | 'courtesy' | 'unavailable' | 'blocked';

/**
 * Asiento procesado para el mapa
 */
export interface SeatDisplay {
  ticket: Ticket;
  status: SeatStatus;
  price?: Precio;
}

/**
 * Item seleccionado en el carrito
 */
export interface SelectedSeat {
  ticket: Ticket;
  price: Precio;
}

/**
 * Resumen de selección
 */
export interface SelectionSummary {
  seats: SelectedSeat[];
  count: number;
  total: number;
}

/**
 * Componente reutilizable para mostrar y gestionar un mapa de asientos
 * Diseñado para trabajar con datos de TipoTicketDetailed
 */
@Component({
  selector: 'app-seat-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './seat-map.component.html',
  styleUrl: './seat-map.component.css',
})
export class SeatMapComponent implements OnInit {
  /**
   * Datos del tipo de ticket con seatmap y tickets
   */
  @Input({ required: true }) tipoTicket!: TipoTicketDetailed;

  /**
   * Precio seleccionado para los tickets (opcional, puede ser null al inicio)
   */
  @Input() selectedPrice?: Precio;

  /**
   * Evento emitido cuando se selecciona o deselecciona un asiento
   */
  @Output() seatClicked = new EventEmitter<Ticket>();

  /**
   * Evento emitido cuando cambia la selección
   */
  @Output() selectionChanged = new EventEmitter<SelectionSummary>();

  // Signals para estado reactivo
  protected readonly seatMatrix = signal<(SeatDisplay | null)[][]>([]);
  protected readonly selectedSeats = signal<Set<string>>(new Set());

  // Computed para el resumen de selección
  protected readonly selectionSummary = computed<SelectionSummary>(() => {
    const selected = this.selectedSeats();
    const seats: SelectedSeat[] = [];
    let total = 0;

    selected.forEach(ticketId => {
      const ticket = this.tipoTicket.tickets.find(t => t.ticketid === ticketId);
      if (ticket && this.selectedPrice) {
        seats.push({ ticket, price: this.selectedPrice });
        total += this.selectedPrice.precio_total;
      }
    });

    return {
      seats,
      count: seats.length,
      total
    };
  });

  // Effect para emitir cambios en la selección
  private selectionEffect = effect(() => {
    const summary = this.selectionSummary();
    this.selectionChanged.emit(summary);
  });

  ngOnInit(): void {
    console.log('🎯 SeatMapComponent ngOnInit');
    console.log('  - tipoTicket:', this.tipoTicket);
    console.log('  - selectedPrice:', this.selectedPrice);

    if (!this.tipoTicket) {
      console.error('❌ SeatMapComponent: tipoTicket es requerido pero no fue proporcionado');
      throw new Error('SeatMapComponent requires [tipoTicket] input');
    }

    console.log('  - Tiene seatmap:', !!this.tipoTicket.seatmap);
    if (this.tipoTicket.seatmap) {
      console.log('  - Seatmap:', {
        id: this.tipoTicket.seatmap.seatmapid,
        nombre: this.tipoTicket.seatmap.nombre,
        rows: this.tipoTicket.seatmap.rows,
        cols: this.tipoTicket.seatmap.cols,
        seats: this.tipoTicket.seatmap.seats.length
      });
    }
    console.log('  - Total tickets:', this.tipoTicket.tickets.length);

    // Diferir la construcción de la matriz al siguiente ciclo de detección
    setTimeout(() => {
      this.buildSeatMatrix();
      console.log('✅ SeatMapComponent inicializado - Matriz construida con', this.seatMatrix().length, 'filas');
    }, 0);
  }

  /**
   * Construye la matriz de asientos basada en el seatmap
   */
  private buildSeatMatrix(): void {
    console.log('🔨 Construyendo matriz de asientos...');

    if (!this.tipoTicket.seatmap) {
      console.warn('⚠️ No seatmap available for this ticket type');
      return;
    }

    const { rows, cols } = this.tipoTicket.seatmap;
    console.log(`  - Dimensiones: ${rows} filas x ${cols} columnas`);

    const matrix: (SeatDisplay | null)[][] = [];

    // Inicializar matriz vacía
    for (let row = 0; row < rows; row++) {
      matrix[row] = [];
      for (let col = 0; col < cols; col++) {
        matrix[row][col] = null;
      }
    }
    console.log('  - Matriz vacía inicializada');

    // Llenar matriz con tickets
    let ticketsColocados = 0;
    this.tipoTicket.tickets.forEach((ticket, index) => {
      if (ticket.row !== undefined && ticket.col !== undefined) {
        const status = this.getTicketStatus(ticket);
        matrix[ticket.row][ticket.col] = {
          ticket,
          status,
          price: this.selectedPrice || this.tipoTicket.precios[0] // Usar primer precio como placeholder
        };
        ticketsColocados++;
      } else {
        console.warn(`  - Ticket ${index} (${ticket.ticketid}) no tiene row/col definidos:`, ticket);
      }
    });

    console.log(`  - Asientos colocados en matriz: ${ticketsColocados} de ${this.tipoTicket.tickets.length} tickets`);
    this.seatMatrix.set(matrix);
    console.log('✅ Matriz de asientos construida exitosamenteSEATMAP');
  }

  /**
   * Determina el estado de un ticket
   */
  private getTicketStatus(ticket: Ticket): SeatStatus {
    const estado = ticket.estado.toLowerCase();

    if (estado === 'disponible') return 'available';
    if (estado === 'reservado')  return 'reserved';
    if (estado === 'bloqueado')  return 'blocked';
    if (estado === 'vendido' || estado === 'cortesia') return 'sold';

    return 'reserved';
  }

  /**
   * Maneja el click en un asiento
   */
  onSeatClick(seat: SeatDisplay): void {
    if (seat.status !== 'available' && seat.status !== 'selected') {
      return; // No permitir seleccionar asientos no disponibles ni bloqueados
    }

    this.seatClicked.emit(seat.ticket);
  }

  /**
   * Actualiza el estado visual de un asiento en la matriz
   */
  private updateSeatStatus(ticket: Ticket, newStatus: SeatStatus): void {
    const matrix = this.seatMatrix();
    if (ticket.row !== undefined && ticket.col !== undefined) {
      const seat = matrix[ticket.row][ticket.col];
      if (seat) {
        seat.status = newStatus;
        // Crear nueva referencia para activar detección de cambios
        this.seatMatrix.set([...matrix]);
      }
    }
  }

  /**
   * Verifica si un asiento está seleccionado
   */
  isSeatSelected(seat: SeatDisplay): boolean {
    // Solo considerar selección local para asientos realmente seleccionables.
    return (seat.status === 'available' || seat.status === 'selected')
      && this.selectedSeats().has(seat.ticket.ticketid);
  }

  /**
   * Obtiene las clases CSS para un asiento
   */
  getSeatClasses(seat: SeatDisplay): string {
    const classes = ['seat'];

    if (this.isSeatSelected(seat)) {
      classes.push('seat-selected');
    } else {
      classes.push(`seat-${seat.status}`);
    }

    return classes.join(' ');
  }

  /**
   * Obtiene la ruta de la imagen SVG según el estado del asiento
   */
  getSeatImageSrc(seat: SeatDisplay): string {
    const status = this.isSeatSelected(seat) ? 'selected' : seat.status;

    const imageMap: Record<SeatStatus, string> = {
      available:       '/assets/seatmap/available.svg',
      selected:        '/assets/seatmap/selected.svg',
      reserved:        '/assets/seatmap/unavailable.svg',
      'cart-reserved': '/assets/seatmap/unavailable.svg',
      sold:            '/assets/seatmap/sold.svg',
      validated:       '/assets/seatmap/validated.svg',
      cancelled:       '/assets/seatmap/anulado.svg',
      courtesy:        '/assets/seatmap/cortesia.svg',
      unavailable:     '/assets/seatmap/unavailable.svg',
      blocked:         '/assets/seatmap/bloqueado.svg'
    };

    return imageMap[status] || '/assets/seatmap/unavailable.svg';
  }

  /**
   * Determina si una celda es un espacio vacío
   */
  isEmptySpace(seat: SeatDisplay | null): boolean {
    return seat === null;
  }

  /**
   * Obtiene la etiqueta del asiento (usando secuencial)
   */
  getSeatLabel(seat: SeatDisplay): string {
    return seat.ticket.secuencial || `${seat.ticket.row}-${seat.ticket.col}`;
  }

  /**
   * Limpia todas las selecciones
   */
  public clearSelection(): void {
    const matrix = this.seatMatrix();
    const selected = this.selectedSeats();

    // Actualizar estados a available
    selected.forEach(ticketId => {
      const ticket = this.tipoTicket.tickets.find(t => t.ticketid === ticketId);
      if (ticket) {
        this.updateSeatStatus(ticket, 'available');
      }
    });

    this.selectedSeats.set(new Set());
  }

  /**
   * Obtiene el resumen de selección actual
   */
  public getSelectionSummary(): SelectionSummary {
    return this.selectionSummary();
  }

  /**
   * Obtiene los IDs de los asientos seleccionados
   */
  public getSelectedSeatIds(): string[] {
    return Array.from(this.selectedSeats());
  }

  /**
   * Actualiza el precio para recalcular totales
   */
  public updatePrice(newPrice: Precio): void {
    this.selectedPrice = newPrice;
    // Forzar recálculo del computed
    this.selectedSeats.set(new Set(this.selectedSeats()));
  }

  /**
   * Marca un asiento como vendido/reservado (para actualizar después de agregar al carrito)
   */
  public markSeatAsReserved(ticketId: string): void {
    const ticket = this.tipoTicket.tickets.find(t => t.ticketid === ticketId);
    if (ticket && ticket.row !== undefined && ticket.col !== undefined) {
      const matrix = this.seatMatrix();
      const seat = matrix[ticket.row][ticket.col];
      if (seat) {
        seat.status = 'cart-reserved';
        seat.ticket.estado = 'reservado';
        const selected = new Set(this.selectedSeats());
        if (selected.has(ticketId)) {
          selected.delete(ticketId);
          this.selectedSeats.set(selected);
        }
        this.seatMatrix.set([...matrix]);
      }
    }
  }

  /**
   * Actualiza el estado de un asiento según su ticketId y el nuevo estado
   */
  public updateSeatStatusByTicketId(ticketId: string, estado: string, isInCart = false): void {
    const ticket = this.tipoTicket.tickets.find(t => t.ticketid === ticketId);
    if (ticket && ticket.row !== undefined && ticket.col !== undefined) {
      const matrix = this.seatMatrix();
      const seat = matrix[ticket.row][ticket.col];
      if (seat) {
        // Mapear estado a status visual
        switch (estado) {
          case 'bloqueado':
            seat.status = 'blocked';
            break;
          case 'disponible':
            seat.status = 'available';
            break;
          case 'reservado':
            seat.status = isInCart ? 'cart-reserved' : 'reserved';
            break;
          case 'vendido':
            seat.status = 'sold';
            break;
          case 'validado':
            seat.status = 'validated';
            break;
          case 'anulado':
            seat.status = 'cancelled';
            break;
          case 'cortesia':
            seat.status = 'courtesy';
            break;
          default:
            seat.status = 'unavailable';
        }
        seat.ticket.estado = estado;
        if (seat.status !== 'available') {
          const selected = new Set(this.selectedSeats());
          if (selected.has(ticketId)) {
            selected.delete(ticketId);
            this.selectedSeats.set(selected);
          }
        }
        this.seatMatrix.set([...matrix]);
      }
    }
  }

  /**
   * Marca un asiento como vendido
   */
  public markSeatAsSold(ticketId: string): void {
    this.updateSeatStatusByTicketId(ticketId, 'vendido');
  }

  /**
   * Marca un asiento como validado
   */
  public markSeatAsValidated(ticketId: string): void {
    this.updateSeatStatusByTicketId(ticketId, 'validado');
  }

  /**
   * Marca un asiento como anulado
   */
  public markSeatAsCancelled(ticketId: string): void {
    this.updateSeatStatusByTicketId(ticketId, 'anulado');
  }

  /**
   * Marca un asiento como cortesía
   */
  public markSeatAsCourtesy(ticketId: string): void {
    this.updateSeatStatusByTicketId(ticketId, 'cortesia');
  }
  /**
   * Libera un asiento y lo marca como disponible
   */
  public markSeatAsAvailable(ticketId: string): void {
    const ticket = this.tipoTicket.tickets.find(t => t.ticketid === ticketId);

    if (ticket && ticket.row !== undefined && ticket.col !== undefined) {
      const matrix = this.seatMatrix();
      const seat = matrix[ticket.row][ticket.col];

      if (seat) {
        // Cambiar estado visual
        seat.status = 'available';

        // Actualizar estado del ticket
        seat.ticket.estado = 'disponible';

        // Quitar de seleccionados si estaba
        const selected = new Set(this.selectedSeats());
        if (selected.has(ticketId)) {
          selected.delete(ticketId);
          this.selectedSeats.set(selected);
        }

        // Forzar detección de cambios
        this.seatMatrix.set([...matrix]);
      }
    }
  }


  /**
   * Items para la leyenda
   */
  getLegendItems() {
    return [
      {
        status: 'available',
        label: 'Disponible',
        class: 'seat-available',
      },
      {
        status: 'selected',
        label: 'Seleccionado',
        class: 'seat-selected',
      },
      {
        status: 'reserved',
        label: 'Reservado',
        class: 'seat-reserved',
      },
      {
        status: 'sold',
        label: 'Vendido',
        class: 'seat-sold',
      }
    ];
  }
}
